/**
 * Report-column registry ⇄ shared filter panel.
 *
 * The Meetings list drives `@/components/ui/filter-panel`'s `FilterButton`
 * (the same control the jobs list uses), which speaks `ActiveFilter`
 * (`field`/`operator`/`value`, all strings). The API speaks `FilterIn`
 * (`column`/`op`/`value`, typed). These helpers translate both ways and are
 * pure so they can be unit-tested without rendering the panel.
 */

import { endOfDay, format, startOfDay } from "date-fns";

import {
  parseDateRange,
  type ActiveFilter,
  type FilterFieldDef,
  type FilterFieldOption,
  type FilterOperator,
} from "@/components/ui/filter-panel";
import type { CentralIconName } from "@/types/central-icon";
import type { ColumnOut, ColumnsOut, FilterIn, FilterInOp } from "@/lib/api/model";

import { COPY } from "./copy";
import {
  CONVERSATION_STATES,
  MEETING_TYPES,
  meetingTypeLabel,
  stateLabel,
  type Tone,
} from "./labels";

const OP_PREFERENCE: Record<string, FilterInOp[]> = {
  text: ["contains", "eq", "neq"],
  enum: ["in", "eq"],
  date: ["between", "gte", "lte"],
  datetime: ["between", "gte", "lte"],
  bool: ["eq"],
  person: ["eq"],
  entity: ["eq"],
  number: ["eq", "gte", "lte", "between"],
};

/** Pick the first preferred op that the registry actually allows. */
export function pickFilterOp(column: ColumnOut): FilterInOp | null {
  const prefs = OP_PREFERENCE[column.type] ?? ["contains", "eq"];
  const allowed = new Set(column.ops);
  return prefs.find((op) => allowed.has(op)) ?? null;
}

export function asPair(value: unknown): [string | null, string | null] {
  if (!Array.isArray(value) || value.length < 2) return [null, null];
  const a = value[0];
  const b = value[1];
  return [typeof a === "string" ? a : null, typeof b === "string" ? b : null];
}

export function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];
}

export function encodeDateRange(
  type: string,
  from: Date | null,
  to: Date | null,
): [string | null, string | null] {
  if (type === "datetime") {
    return [
      from ? startOfDay(from).toISOString() : null,
      to ? endOfDay(to).toISOString() : null,
    ];
  }
  return [
    from ? format(from, "yyyy-MM-dd") : null,
    to ? format(to, "yyyy-MM-dd") : null,
  ];
}

export function isFilterComplete(f: FilterIn): boolean {
  if (f.op === "is_null" || f.op === "is_not_null") return true;
  if (f.op === "between") return asPair(f.value).every(Boolean);
  if (f.op === "in") return asStringArray(f.value).length > 0;
  return f.value !== "" && f.value !== null && f.value !== undefined;
}

/* ── Field definitions ──────────────────────────────────────────────── */

const TONE_BADGE: Record<Tone, { variant: string; className: string }> = {
  neutral: {
    variant: "outline-muted",
    className: "border-border bg-contrast/4 text-contrast/50",
  },
  info: { variant: "info", className: "border-border" },
  success: { variant: "success", className: "border-border" },
  warning: { variant: "warning", className: "border-border" },
  danger: {
    variant: "outline-muted",
    className: "border-destructive/20 bg-destructive/10 text-destructive",
  },
};

const FIELD_ICONS: Record<string, CentralIconName> = {
  title: "IconPencil",
  state: "IconCircleCheck",
  meeting_type: "IconVideo",
  capture_mode: "IconRecord",
  data_class: "IconShield",
  scheduled_start: "IconCalendar1",
  scheduled_end: "IconCalendar1",
  duration: "IconClock",
  archived: "IconArchive",
};

const TYPE_ICONS: Record<string, CentralIconName> = {
  text: "IconPencil",
  enum: "IconCircleCheck",
  bool: "IconCircleCheck",
  date: "IconCalendar1",
  datetime: "IconCalendar1",
  number: "IconClock",
};

function fieldIcon(column: ColumnOut): CentralIconName {
  return (
    FIELD_ICONS[column.id] ?? TYPE_ICONS[column.type] ?? "IconCircleInfo"
  );
}

/**
 * Option lists the registry itself doesn't carry. `ColumnOut` has no enum
 * values, so only the two enums with a label map in `lib/labels.ts` can offer
 * a picker; any other enum column is left out of the panel rather than
 * shipping a free-text box that silently matches nothing (same reasoning as
 * person/entity columns, which need an id picker we don't have in 02).
 */
function enumOptions(column: ColumnOut): FilterFieldOption[] | null {
  if (column.id === "state") {
    return CONVERSATION_STATES.map((state) => {
      const meta = stateLabel(state);
      const badge = TONE_BADGE[meta.tone];
      return {
        value: state,
        label: meta.label,
        badgeVariant: badge.variant,
        badgeClassName: badge.className,
      };
    });
  }
  if (column.id === "meeting_type") {
    return MEETING_TYPES.map((key) => ({
      value: key,
      label: meetingTypeLabel(key),
    }));
  }
  return null;
}

function fieldType(column: ColumnOut): FilterFieldDef["type"] | null {
  switch (column.type) {
    case "text":
      return "text";
    case "number":
      return "number";
    case "date":
    case "datetime":
      return "date_range";
    case "bool":
    case "enum":
      return "select";
    default:
      return null;
  }
}

/** Registry → panel fields, in registry order. */
export function columnsToFilterFields(registry: ColumnsOut): FilterFieldDef[] {
  const columns = [...registry.builtins, ...registry.custom];
  const fields: FilterFieldDef[] = [];
  for (const column of columns) {
    if (column.type === "person" || column.type === "entity") continue;
    if (pickFilterOp(column) === null) continue;
    const type = fieldType(column);
    if (!type) continue;
    let options: FilterFieldOption[] = [];
    if (type === "select") {
      if (column.type === "bool") {
        options = [
          { value: "true", label: COPY.filters.on },
          { value: "false", label: COPY.filters.off },
        ];
      } else {
        const enums = enumOptions(column);
        if (!enums) continue;
        options = enums;
      }
    }
    fields.push({
      key: column.id,
      label: column.label,
      icon: fieldIcon(column),
      type,
      options,
      placeholder: type === "text" ? COPY.filters.value : undefined,
    });
  }
  return fields;
}

export function columnIndex(registry: ColumnsOut): Map<string, ColumnOut> {
  return new Map(
    [...registry.builtins, ...registry.custom].map((c) => [c.id, c]),
  );
}

/* ── ActiveFilter → FilterIn ────────────────────────────────────────── */

function resolveOp(
  column: ColumnOut,
  operator: FilterOperator,
): FilterInOp | null {
  const allowed = new Set(column.ops);
  const first = (...ops: FilterInOp[]) => ops.find((op) => allowed.has(op)) ?? null;
  switch (operator) {
    case "is":
      return column.type === "enum" || column.type === "bool"
        ? first("in", "eq")
        : first("eq", "in");
    case "is_not":
      return first("neq");
    case "contains":
      return first("contains", "eq");
    case "between":
      return first("between", "gte");
    // The API has no strict gt/lt, so the panel's strict bounds fall back to
    // their inclusive siblings.
    case "gt":
    case "gte":
      return first("gte");
    case "lt":
    case "lte":
      return first("lte");
    default:
      return null;
  }
}

function coerceValue(column: ColumnOut, raw: string): unknown {
  if (column.type === "number") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : "";
  }
  if (column.type === "bool") return raw === "true";
  return raw;
}

export function activeFilterToFilterIn(
  column: ColumnOut,
  filter: ActiveFilter,
): FilterIn | null {
  const op = resolveOp(column, filter.operator);
  if (!op) return null;

  if (column.type === "date" || column.type === "datetime") {
    const range = parseDateRange(filter.value);
    if (!range?.from) return null;
    const [from, to] = encodeDateRange(
      column.type,
      range.from,
      range.to ?? null,
    );
    if (op === "between") {
      if (!from || !to) return null;
      return { column: column.id, op, value: [from, to] };
    }
    return { column: column.id, op, value: op === "lte" ? to : from };
  }

  if (filter.value === "") return null;
  const value =
    op === "in"
      ? [coerceValue(column, filter.value)]
      : coerceValue(column, filter.value);
  const candidate: FilterIn = { column: column.id, op, value };
  return isFilterComplete(candidate) ? candidate : null;
}

export function activeFiltersToFilterIn(
  registry: ColumnsOut,
  filters: ActiveFilter[],
): FilterIn[] {
  const byId = columnIndex(registry);
  const out: FilterIn[] = [];
  for (const filter of filters) {
    const column = byId.get(filter.field);
    if (!column) continue;
    const mapped = activeFilterToFilterIn(column, filter);
    if (mapped) out.push(mapped);
  }
  return out;
}

/* ── FilterIn → ActiveFilter ────────────────────────────────────────── */

const OP_TO_OPERATOR: Partial<Record<FilterInOp, FilterOperator>> = {
  eq: "is",
  in: "is",
  neq: "is_not",
  contains: "contains",
  between: "between",
  gte: "gte",
  lte: "lte",
};

function decodeDate(value: unknown): string {
  if (typeof value !== "string" || !value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

/** Hydrate the panel from the `f` search param so chips survive a reload. */
export function filterInToActiveFilters(
  registry: ColumnsOut,
  filters: FilterIn[],
): ActiveFilter[] {
  const byId = columnIndex(registry);
  const out: ActiveFilter[] = [];
  for (const filter of filters) {
    const column = byId.get(filter.column);
    if (!column) continue;
    const operator = OP_TO_OPERATOR[filter.op];
    if (!operator) continue;
    if (column.type === "date" || column.type === "datetime") {
      const [from, to] = filter.op === "between"
        ? asPair(filter.value)
        : [
            filter.op === "lte" ? null : (filter.value as string | null),
            filter.op === "lte" ? (filter.value as string | null) : null,
          ];
      const encodedFrom = decodeDate(from);
      const encodedTo = decodeDate(to);
      if (!encodedFrom && !encodedTo) continue;
      out.push({
        field: column.id,
        operator,
        value: `${encodedFrom}|${encodedTo}`,
      });
      continue;
    }
    const raw = filter.op === "in" ? asStringArray(filter.value)[0] : filter.value;
    if (raw === undefined || raw === null || raw === "") continue;
    out.push({ field: column.id, operator, value: String(raw) });
  }
  return out;
}
